import React, { FC, memo, useCallback, useEffect } from 'react'

import { useSetFocus } from '@please/lrud'
import { useLocation } from 'react-router-dom'

import { SearchService } from '@sportworld/js-sdk/src/service/search'

import { getLineDataById } from '@/actions/actions'
import { List } from '@/components/List/List'
import { MainSidebar } from '@/components/MainSidebar/MainSidebar'
import { PageBackground } from '@/components/PageContent/PageBackground'
import { PageHeader } from '@/components/PageHeader/PageHeader'
import { LineTileShape } from '@/constants/common'
import { focusIds } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { PopupBG } from '@/containers/EventDetailPage/styles'
import { PageContainer } from '@/containers/Search/styles'
import { useCustomPalette } from '@/hooks/useSetPalette'
import { usePPVPopupStore } from '@/store/usePPVPopupStore'
import useStore from '@/store/useStore'
import { TileType } from '@/types/servicesTypes/configTypes'
import { SearchQueryParams } from '@/types/servicesTypes/searchTypes'
import { handlePageViewAnalyticsEvent } from '@/utils/analyticsUtils'
import {
  getTimezoneOffsetReversed,
  sendPageImpressionToGTAG,
} from '@/utils/utils'

const focusId = `${focusIds.LIST}_${focusIds.POPUP}${focusIds.CONTAINER_MOD}`

const ListPopupRaw: FC = () => {
  const { boughtEventIds, removeUpdatedEventIds } = usePPVPopupStore.getState()
  const listPopup = useStore((state) => state.common.listPopup)
  const currentEvent = useStore((state) => state.common.currentEvent)
  const setListPopup = useStore((state) => state.common.setListPopup)
  const setFocus = useSetFocus()
  const { pathname } = useLocation()

  const { pagePopup } = useStore((state) => state.popup)
  const pageFocusId = `${focusIds.PAGE_POPUP}${focusIds.LIST}${focusIds.CONTAINER_MOD}`

  useEffect(() => {
    return () => {
      setListPopup(null)
    }
  }, [])

  const onBack = () => {
    void sendPageImpressionToGTAG(pathname)
    handlePageViewAnalyticsEvent(pathname)
    setListPopup(null)
    const focusId = currentEvent
      ? `${focusIds.EVENT_PAGE}_${focusIds.POPUP}${focusIds.CONTAINER_MOD}`
      : pagePopup
      ? `${focusIds.PAGE}_${focusIds.POPUP}${focusIds.CONTAINER_MOD}`
      : focusIds.CONTENT
    setFocus(focusId)
  }

  const { pageImgSrc } = useCustomPalette()

  const timezoneOffsetReversed = getTimezoneOffsetReversed()

  const fetchData = useCallback(
    async (fetchLimit: number, next: string, abortSignal?: AbortSignal) => {
      if (!listPopup || !listPopup.listContextId) {
        return
      }
      const params: SearchQueryParams = {
        term: listPopup.listContextId,
        next,
        type: listPopup.searchResultType || 'event',
        limit: fetchLimit,
      }

      let lineData = listPopup.isSearchListType
        ? await SearchService.searchQuery<TileType>(params, true)
        : await getLineDataById(
            listPopup.listContextId,
            fetchLimit,
            next,
            timezoneOffsetReversed,
            abortSignal,
          )

      if (boughtEventIds.length) {
        const updatedEventIds: string[] = []

        lineData = {
          ...lineData,
          items: lineData.items.map((tile) => {
            const event = tile.data?.event
            if (event?.id && boughtEventIds.includes(event.id)) {
              if (event.userRights?.canAccess) {
                updatedEventIds.push(event.id)
                return tile
              }
              return {
                ...tile,
                data: {
                  ...tile.data,
                  event: {
                    ...event,
                    userRights: {
                      ...event.userRights,
                      canAccess: true,
                      reasons: [{ type: 'Ppv' }],
                    },
                  },
                },
              } as TileType
            }
            return tile
          }),
        }

        removeUpdatedEventIds(updatedEventIds)
      }
      return lineData
    },
    [
      listPopup?.listContextId,
      timezoneOffsetReversed,
      listPopup?.isSearchListType,
      listPopup?.searchResultType,
    ],
  )

  if (!listPopup) {
    return null
  }

  const listPageScrollOptions =
    scrollOptions.ListPageScroll[
      listPopup.tileShape || LineTileShape.ASPECT16x9
    ]

  return (
    <PopupBG focusId={focusId} isTrap>
      {pageImgSrc && <PageBackground src={pageImgSrc} />}
      <MainSidebar
        focusIdPrefix={focusId}
        onBack={onBack}
        disableSubMenuAutoOpen
      />
      <PageContainer focusId={pageFocusId} className="content-wrapper">
        <PageHeader name={listPopup.name} />
        {listPopup?.listContextId && (
          <List
            pageFocusId={pageFocusId}
            listContextId={listPopup.listContextId}
            isSearchListType={listPopup.isSearchListType}
            name={listPopup.name}
            tileShape={listPopup.tileShape}
            searchResultType={listPopup.searchResultType}
            activeTileIndex={listPopup.activeTileIndex}
            activeTileId={listPopup.activeTileId}
            fetchData={fetchData}
            scrollOptions={listPageScrollOptions}
            isListPopup
            showScrollbar
          />
        )}
      </PageContainer>
    </PopupBG>
  )
}

export const ListPopup = memo(ListPopupRaw)
